// apps/web/src/features/call-center/components/CallCenterLeadCard.tsx
//
// Mobile card for the external agent's own-leads list (#1398). A single
// tappable surface that opens the read-only detail drawer full-screen; the
// recording anchor stops propagation so it opens the link without firing the
// card tap.
//
// DANGER: external boundary — render ONLY the blind snapshot fields.
import { useTranslation } from 'react-i18next';
import type { CallCenterMyLeadsItem } from '@magimp/shared';
import { useInteractiveRow } from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';

export function CallCenterLeadCard({
  lead,
  onActivate,
}: {
  lead: CallCenterMyLeadsItem;
  onActivate: () => void;
}) {
  const { t } = useTranslation('callCenter');
  const { background, handlers } = useInteractiveRow({ selected: false, onActivate });

  return (
    <div
      {...handlers}
      aria-label={lead.name || lead.phone}
      style={{
        background: background === 'transparent' ? 'var(--bg-1)' : background,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 'var(--s-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>{lead.name}</span>
        <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>{lead.phone}</span>
      </div>
      {lead.comment ? (
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>{lead.comment}</div>
      ) : null}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--s-3)',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{formatDateTime(lead.createdAt)}</span>
        {lead.audioUrl ? (
          <a
            href={lead.audioUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 12, color: 'var(--accent-1)', textDecoration: 'none' }}
          >
            {t('selfService.audioLink')}
          </a>
        ) : null}
      </div>
    </div>
  );
}
